
import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import {
  alertModule, currentUserModule,
  dataFieldsEditModule,
  studyDefinitionModule,
  studyInstitutesModule,
  studyModule, studyPatientContext,
  studyPatientModule,
  visitModule
} from "@/store";
import DataFields from "@/components/study/DataFields.vue";
import {convertToCreateDtos, findRedirectRuleFields} from "@/auxillary/datafield.cache.helpers";
import {MdbsCore} from "@/api/tsclient";
import VisitDefinitionDto = MdbsCore.VisitDefinitionDto;
import {translate} from "@/auxillary/translation";
import {VisitState} from "@/auxillary/visit.states";
import {deepCopy} from "@/auxillary";
import IStudyInstituteShortDto = MdbsCore.IStudyInstituteShortDto;
import Rights = MdbsCore.Rights;
import DataFieldCache from "@/models/data.field.cache";
import InstrumentContextCache from "@/models/instrument.context.cache";
import VisitContextCache from "@/models/visit.context.cache";
import {Dictionary} from "vue-router/types/router";
import YesNoCancelDialog from "@/components/common/YesNoCancelDialog.vue";

@Component({
  components: {
    DataFields,
    YesNoCancelDialog
  }
})
export default class VisitNewView extends Vue {

  currentVisitContext: Readonly<VisitContextCache> | null = null;
  saving = false;
  visitDefinition = new VisitDefinitionDto();
  private snackbarText = "";
  private showSnackbar = false;
  private snackbarColor = "success";

  @Prop({default: ""}) private patientId: string;
  @Prop({default: ""}) private visitDefinitionId: string;

  showRedirectDialog = false;
  redirectDialogText = "";
  redirectDialogSubject = "";
  currentRedirectConfig: any = {};

  @Watch('currentRedirectConfig', {immediate: true, deep: true})
  private onRedirectConfigChanged() {
    if (this.currentRedirectConfig?.field === undefined) {
      this.redirectDialogText = "";
      return;
    }

    this.redirectDialogSubject = translate(this.currentRedirectConfig.field.redirectRule?.definition?.subjectDisplayKey);
    this.redirectDialogText = translate(this.currentRedirectConfig.field.redirectRule?.definition?.textDisplayKey);
  }

  get loading() {
    return studyInstitutesModule.isLoading ||
        studyModule.isLoading ||
        studyDefinitionModule.isLoading ||
        dataFieldsEditModule.isLoading;
  }

  get patientDisplayName(){
    return studyPatientModule.studyPatient?.displayName ?? ""
  }

  get newVisitWording(){
    if (this.visitDefinition.identifier == "VEarlyPatientExclusion")
      return "";

    if (this.visitDefinition.identifier == "VIntermediate")
      return "Neuer";

    return "Neue";
  }

  get visitDisplayName(){
    return translate(this.visitDefinition?.displayKey);
  }

  get studyId() {
    return studyModule.study.id;
  }

  get isValid() {
    // TODO Demo Hack
    // basic check for required field
    return dataFieldsEditModule.dataFields.every(
        d => !d.isVisible || d.values.value != null
    )
  }

  get canSave() {
    return this.isValid && this.isEditable;
  }

  get canCreateVisit() {
    return currentUserModule.hasRight(Rights.VisitCreateCmd)
  }

  get isEditable() {
    return this.canCreateVisit && (this.currentVisitContext?.isEditable ?? false);
  }

  get showNotEditableReason() {
    return (this.currentVisitContext?.isEditable === false) ?? false;
  }

  get notEditableReason() {
    return this.currentVisitContext?.notEditableReason ?? "";
  }

  get institutes() {
    return studyInstitutesModule.studyInstitutes
        .filter((i:IStudyInstituteShortDto) => i.studyId == this.studyId)
        .sort((a:IStudyInstituteShortDto, b:IStudyInstituteShortDto) => (a.displayName ?? "") > (b.displayName ?? "") ? 1 : -1);
  }

  async mounted() {
    if (studyPatientModule.studyPatient.id != this.patientId) {
      await studyPatientModule.loadById(this.patientId)
    }
    this.currentVisitContext = studyPatientContext?.visits?.find(v => v.definition?.id == this.visitDefinitionId) ?? null;
    await studyModule.loadById(studyPatientModule.studyPatient?.studyId ?? "")
    await studyDefinitionModule.loadById(studyModule.study?.studyDefinitionId ?? "")
    this.visitDefinition = studyDefinitionModule.visitDefinitionById(this.visitDefinitionId) ?? new VisitDefinitionDto();
    dataFieldsEditModule.setEditable(this.isEditable)
    await dataFieldsEditModule.loadDataFields({
      definitions: this.visitDefinition?.fields ?? [],
      dataFields: []
    })

    if (!this.currentVisitContext?.isNew && this.currentVisitContext?.id != undefined) {
      await this.$router.push({
        name: "visitedit",
        params: {
          visitId: this.currentVisitContext?.id
        }
      });
    }
  }

  async cancel() {
    await this.$router.back();
  }

  async createVisit() {
    this.saving = true
    try {
      const fields = convertToCreateDtos(dataFieldsEditModule.dataFields)
      const visitDefinition = studyDefinitionModule.visitDefinitionById(this.visitDefinitionId);
      if (visitDefinition === undefined) {
        await alertModule.warning("Cannot find visit definition for: " + this.visitDefinitionId)
        this.saving = false;
        return
      }
      await visitModule.create({
        definitionId: this.visitDefinitionId,
        displayKey: visitDefinition.displayKey,
        identifier: visitDefinition.identifier,
        lockedManually: false,
        order: visitDefinition.order,
        patientId: this.patientId,
        fields
      })

      if (visitModule.visit?.validationMessages != null) {
        // if saving attempt failed, the returning dto needs to be shown with their errors
        await dataFieldsEditModule.loadDataFields({
          definitions: this.visitDefinition?.fields ?? [],
          dataFields: visitModule.visit?.fields ?? [],
          validationMessages: visitModule.visit?.validationMessages,
          temporaryFields: []
        })

        const messages = deepCopy(visitModule.visit?.validationMessages?? [])
        dataFieldsEditModule.setValidationMessages(messages);
      } else {
        dataFieldsEditModule.setValidationMessages([])
      }

      if (visitModule.visit.id != null && visitModule.visit.canSave){
        this.showCreateSucceededSnackBar()
        this.checkShowRedirectDialog();

        if (visitModule.visit.state === VisitState.NOT_DONE && !this.showRedirectDialog){
          await this.goBack();
          return;
        }

        if (!this.showRedirectDialog) {
          await this.$router.push({
            name: "visitinstrument",
            params: {
              patientId: studyPatientModule.studyPatient.id,
              visitId: visitModule.visit.id
            }
          })
        }
      }

      this.showCreateFailedSnackBar();
    }
    finally {
      this.saving = false
    }
  }

async goBack() {
  await this.$router.back();
}

  showCreateSucceededSnackBar() {
    this.snackbarColor = "success";
    this.snackbarText = "Visite wurde erfolgreich angelegt."
    this.showSnackbar = true;
  }

  showCreateFailedSnackBar() {
    if (visitModule.visit?.validationMessages?.some(v => v.messageType == "error"))
      this.snackbarColor = "error";
    else
      this.snackbarColor = "warning";
    this.snackbarText = "Invalide Daten, Visite konnte nicht angelegt werden."
    this.showSnackbar = true;
  }

  private checkShowRedirectDialog() {
    this.currentRedirectConfig = {};

    const redirectFields = findRedirectRuleFields(dataFieldsEditModule.dataFields);
    if (redirectFields.length == 0) return;

    const sortedRedirectFields = redirectFields.sort(function( field1: DataFieldCache, field2: DataFieldCache ){
      return (field2.redirectRule?.definition?.priority ?? 0) - (field1.redirectRule?.definition?.priority ?? 0);
    });

    for (let field of sortedRedirectFields) {
      const targetVisitIdentifier = field.redirectRule?.definition?.targetVisitIdentifier;
      const targetInstrumentIdentifier = field.redirectRule?.definition?.targetInstrumentIdentifier;

      const visitContextCache = targetVisitIdentifier
          ? studyPatientContext.visits.find(v => v.identifier == targetVisitIdentifier)
          : undefined;

      if (visitContextCache) {
        if (visitContextCache.isNew) continue;

        const instrumentContextCache = visitContextCache.visibleInstruments.find(i => i.identifier == targetInstrumentIdentifier);
        if (!instrumentContextCache) continue;

        if (!field.isRedirectVisible) continue;

        this.setCurrentRedirectConfig(field, false, instrumentContextCache, visitContextCache);
        this.showRedirectDialog = true;
        return;
      }

      const instrumentContextCache = studyPatientContext.standaloneInstruments.find(i => i.identifier == targetInstrumentIdentifier);
      if (!instrumentContextCache) continue;

      if (!field.isRedirectVisible) continue;

      this.setCurrentRedirectConfig(field, true, instrumentContextCache, undefined);
      this.showRedirectDialog = true;
      return;
    }
  }

  private setCurrentRedirectConfig(
      field: DataFieldCache,
      isStandaloneInstrument: boolean,
      instrumentContextCache: Readonly<InstrumentContextCache>,
      visitContextCache: Readonly<VisitContextCache> | undefined) {
    this.currentRedirectConfig = {field, isStandaloneInstrument, instrumentContextCache, visitContextCache}
  }

  private async applyRedirectRule() {
    this.showRedirectDialog = false;

    if (!this.currentRedirectConfig.field) {
      return;
    }

    const {isStandaloneInstrument, instrumentContextCache, visitContextCache} = this.currentRedirectConfig;
    if (isStandaloneInstrument) {
      await this.openStandaloneInstrument(instrumentContextCache);
      return;
    }

    await this.openVisitInstrument(visitContextCache, instrumentContextCache);
    return;
  }

  async openStandaloneInstrument(instrumentContext: InstrumentContextCache) {
    if (instrumentContext.isNew) {
      await this.navigate("standaloneinstrumentnew", {
        patientId: this.patientId,
        instrumentDefinitionId: instrumentContext.definitionId
      });
    } else {
      await this.navigate("standaloneinstrumentedit", {
        patientId: this.patientId,
        instrumentId: instrumentContext.id
      });
    }
  }

  async openVisitInstrument(visitContext: VisitContextCache, instrumentContext: InstrumentContextCache) {
    if (instrumentContext.isNew) {
      await this.navigate("visitinstrumentnew", {
        patientId: this.patientId,
        visitId: visitContext.id,
        instrumentDefinitionId: instrumentContext.definitionId
      });
    } else {
      await this.navigate("visitinstrumentedit", {
        patientId: this.patientId,
        visitId: visitContext.id,
        instrumentId: instrumentContext.id
      });
    }
  }

  async navigate(name: string, params: Dictionary<string>) {
    return await this.$router.push({name, params})
        .catch(() => {
          return undefined;
        });
  }

}
